import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';

const hoursSince = (iso) => (Date.now() - new Date(iso).getTime()) / 36e5;
const timeAgo = (iso) => {
  const h = hoursSince(iso);
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min ago`;
  if (h < 24) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
};
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Drawer from '../components/Drawer';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { money, date } from '../lib/format';

const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = (incomeId) => ({ item_id: '', description: '', quantity: 1, unit_price: 0, taxable: true, income_account_id: incomeId || '' });
const cadenceLabel = (c) => (c === 'semi_monthly' ? 'Semi-monthly' : 'Monthly');

export default function Contracts() {
  const { activeId } = useBusiness();
  const { can } = useAuth();
  const editable = can('contracts.manage');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [running, setRunning] = useState(false);

  const [meta, setMeta] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [stripeEnabled, setStripeEnabled] = useState(false);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('new');
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(null);
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docBusy, setDocBusy] = useState(false);
  const [docErr, setDocErr] = useState(null);

  const loadList = useCallback(() => {
    setLoading(true); setError(null);
    api(`/contracts?business_id=${activeId}`)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => { setRows([]); if (e?.status && e.status !== 404) setError('Could not load contracts.'); })
      .finally(() => setLoading(false));
  }, [activeId]);

  useEffect(() => { loadList(); }, [loadList]);
  const [billingStatus, setBillingStatus] = useState(null);
  const loadBillingStatus = useCallback(() => {
    api(`/contracts/billing-status?business_id=${activeId}`).then(setBillingStatus).catch(() => setBillingStatus(null));
  }, [activeId]);
  useEffect(() => { loadBillingStatus(); }, [loadBillingStatus]);
  useEffect(() => {
    api(`/invoices/meta?business_id=${activeId}`).then(setMeta).catch(() => setMeta(null));
    api(`/customers?business_id=${activeId}`).then((d) => setCustomers((Array.isArray(d) ? d : []).filter((c) => c.is_active !== false))).catch(() => setCustomers([]));
    api(`/items?business_id=${activeId}&active=1`).then((d) => setItems(Array.isArray(d) ? d : [])).catch(() => setItems([]));
    api(`/businesses/${activeId}/stripe-status`).then((s) => setStripeEnabled(!!s.enabled)).catch(() => setStripeEnabled(false));
  }, [activeId]);

  const taxRate = meta?.collects_sales_tax ? Number(meta.tax_rate) || 0 : 0;
  const defaultIncome = meta?.default_income_account_id || '';

  const totals = useMemo(() => {
    let subtotal = 0, taxable = 0;
    for (const l of lines) {
      const amt = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
      subtotal += amt;
      if (l.taxable !== false) taxable += amt;
    }
    return { subtotal, tax: taxable * taxRate, total: subtotal + taxable * taxRate };
  }, [lines, taxRate]);

  function openNew() {
    setMode('new'); setCurrent(null);
    setForm({ name: '', customer_id: '', cadence: 'monthly', billing_day_1: 1, billing_day_2: 15, start_date: today(), end_date: '', net_days: 30, auto_finalize: true, auto_charge: false, terms: '' });
    setLines([emptyLine(defaultIncome)]);
    setDocs([]); setDocErr(null);
    setFormError(null); setOpen(true);
  }

  async function openRow(row) {
    setFormError(null); setOpen(true);
    try {
      const c = await api(`/contracts/${row.id}`);
      setCurrent(c); setMode('edit');
      setForm({
        name: c.name, customer_id: c.customer_id, cadence: c.cadence,
        billing_day_1: c.billing_day_1, billing_day_2: c.billing_day_2 || 15,
        start_date: c.start_date?.slice(0, 10) || today(), end_date: c.end_date?.slice(0, 10) || '',
        net_days: c.net_days, auto_finalize: c.auto_finalize, auto_charge: c.auto_charge, terms: c.terms || '',
      });
      setLines((c.lines || []).map((l) => ({
        item_id: '', description: l.description, quantity: Number(l.quantity),
        unit_price: Number(l.unit_price), taxable: l.taxable, income_account_id: l.income_account_id || '',
      })));
    } catch { setOpen(false); }
    loadDocs(row.id);
  }

  function loadDocs(contractId) {
    setDocErr(null);
    api(`/contracts/${contractId}/documents`).then((d) => setDocs(Array.isArray(d) ? d : [])).catch(() => setDocs([]));
  }

  async function uploadDoc(file) {
    if (!file || !current) return;
    setDocBusy(true); setDocErr(null);
    try {
      const data = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => rej(new Error('Could not read the file'));
        r.readAsDataURL(file);
      });
      await api(`/contracts/${current.id}/documents`, { method: 'POST', body: { filename: file.name, content_type: file.type || 'application/pdf', data } });
      loadDocs(current.id);
    } catch (e) { setDocErr(e?.detail || e?.message || 'Upload failed.'); }
    finally { setDocBusy(false); }
  }

  async function deleteDoc(id) {
    setDocBusy(true); setDocErr(null);
    try {
      await api(`/contracts/documents/${id}`, { method: 'DELETE' });
      setDocs((d) => d.filter((x) => x.id !== id));
    } catch (e) { setDocErr(e?.detail || 'Could not delete.'); }
    finally { setDocBusy(false); }
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine(defaultIncome)]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));
  function selectItem(i, itemId) {
    const it = items.find((x) => String(x.id) === String(itemId));
    setLines((ls) => ls.map((l, idx) => {
      if (idx !== i) return l;
      if (!it) return { ...l, item_id: '' };
      return { ...l, item_id: it.id, description: it.description || it.name, unit_price: Number(it.unit_price) || 0, taxable: it.taxable !== false, income_account_id: it.income_account_id || defaultIncome };
    }));
  }

  async function save() {
    if (!form.name.trim()) { setFormError('Give the contract a name.'); return; }
    if (!form.customer_id) { setFormError('Choose a customer.'); return; }
    const clean = lines.filter((l) => l.description.trim());
    if (clean.length === 0) { setFormError('Add at least one line item.'); return; }
    setSaving(true); setFormError(null);
    const body = {
      business_id: activeId, name: form.name.trim(), customer_id: Number(form.customer_id),
      cadence: form.cadence, billing_day_1: Number(form.billing_day_1),
      billing_day_2: form.cadence === 'semi_monthly' ? Number(form.billing_day_2) : null,
      start_date: form.start_date, end_date: form.end_date || null,
      net_days: Number(form.net_days) || 30, auto_finalize: form.auto_charge ? true : !!form.auto_finalize, auto_charge: !!form.auto_charge, terms: form.terms || null,
      lines: clean.map((l) => ({
        description: l.description.trim(), quantity: Number(l.quantity) || 0, unit_price: Number(l.unit_price) || 0,
        taxable: l.taxable !== false, income_account_id: l.income_account_id || defaultIncome,
      })),
    };
    try {
      if (mode === 'edit' && current) await api(`/contracts/${current.id}`, { method: 'PUT', body });
      else await api('/contracts', { method: 'POST', body });
      setOpen(false); loadList();
    } catch (e) { setFormError(e?.detail || 'Could not save the contract.'); }
    finally { setSaving(false); }
  }

  async function setStatus(status) {
    if (!current) return;
    setSaving(true); setFormError(null);
    try {
      const c = await api(`/contracts/${current.id}/status`, { method: 'POST', body: { status } });
      setCurrent(c); loadList();
    } catch (e) { setFormError(e?.detail || 'Could not update status.'); }
    finally { setSaving(false); }
  }

  async function runBillingNow() {
    setRunning(true); setNotice(null);
    try {
      const s = await api('/contracts/run', { method: 'POST', body: { business_id: activeId } });
      const parts = [`Billing run for ${date(s.run_date)}: ${s.invoices} invoice${s.invoices === 1 ? '' : 's'} created`];
      if (s.skipped) parts.push(`${s.skipped} already billed`);
      if (s.errors?.length) parts.push(`${s.errors.length} error${s.errors.length === 1 ? '' : 's'}`);
      setNotice(parts.join(' · ') + '.');
      loadList(); loadBillingStatus();
    } catch (e) { setNotice(e?.detail || 'Billing run failed.'); }
    finally { setRunning(false); }
  }

  const columns = [
    { key: 'name', header: 'Contract' },
    { key: 'customer', header: 'Customer', render: (r) => r.customer_name || '—' },
    { key: 'cadence', header: 'Cadence', render: (r) => cadenceLabel(r.cadence) },
    { key: 'next_run_date', header: 'Next run', render: (r) => (r.next_run_date ? date(r.next_run_date) : '—') },
    { key: 'period_amount', header: 'Per period', render: (r) => <span className="num">{money(r.period_amount)}</span> },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  const title = mode === 'new' ? 'New contract' : current ? current.name : 'Contract';
  const footer = (
    <>
      <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
      {editable && <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create contract'}</button>}
    </>
  );

  return (
    <>
      <PageHeader
        title="Contracts"
        actions={editable && (
          <div className="header-actions">
            <button className="btn" onClick={runBillingNow} disabled={running}>{running ? 'Running…' : 'Run billing now'}</button>
            <button className="btn btn--primary" onClick={openNew}>New contract</button>
          </div>
        )}
      />
      {notice && <div className="notice">{notice}<button className="notice__x" onClick={() => setNotice(null)}>×</button></div>}
      {billingStatus && (() => {
        const lr = billingStatus.last_run;
        const stale = lr && billingStatus.scheduled_enabled && hoursSince(lr.ran_at) > 26;
        if (!lr) {
          return <div className="hint" style={{ margin: '0 0 10px' }}>
            No billing run recorded yet.{billingStatus.scheduled_enabled ? ' Scheduled billing is configured — it should log its first run within a day.' : ' Automated billing isn’t set up (no scheduled task).'}
          </div>;
        }
        return <div className={stale ? 'form-error' : 'hint'} style={{ margin: '0 0 10px' }}>
          {stale ? '⚠ ' : ''}Last billing run {timeAgo(lr.ran_at)} ({new Date(lr.ran_at).toLocaleString()})
          {' · '}{lr.trigger === 'scheduled' ? 'scheduled task' : 'manual'}
          {' · '}{lr.invoices} invoice{lr.invoices === 1 ? '' : 's'}{lr.errors ? ` · ${lr.errors} error${lr.errors === 1 ? '' : 's'}` : ''}
          {stale && ' — no run in over a day; check the Windows scheduled task.'}
        </div>;
      })()}
      <section className="panel">
        <DataTable columns={columns} rows={rows} loading={loading} error={error}
          getRowKey={(r) => r.id} onRowClick={openRow} empty="No contracts yet. Create one to bill a customer on a schedule." />
      </section>

      <Drawer open={open} title={title} width={760} onClose={() => !saving && setOpen(false)} footer={form && footer}>
        {form && (
          <>
            {formError && <div className="form-error">{formError}</div>}

            {current && mode !== 'new' && (
              <div className="inv-status-row">
                <StatusBadge status={current.status} />
                {current.next_run_date && current.status === 'active' && <span className="hint">Next run {date(current.next_run_date)}</span>}
              </div>
            )}

            <div className="field"><label>Contract name *</label>
              <input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="e.g. Managed IT — monthly" /></div>

            <div className="field"><label>Customer *</label>
              <select value={form.customer_id} onChange={(e) => setField('customer_id', e.target.value)}>
                <option value="">Select a customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
              </select>
            </div>

            <div className="form-grid">
              <div className="field"><label>Cadence</label>
                <select value={form.cadence} onChange={(e) => setField('cadence', e.target.value)}>
                  <option value="monthly">Monthly</option>
                  <option value="semi_monthly">Semi-monthly (twice a month)</option>
                </select>
              </div>
              <div className="field"><label>Billing day{form.cadence === 'semi_monthly' ? ' 1' : ''}</label>
                <input type="number" min="1" max="31" value={form.billing_day_1} onChange={(e) => setField('billing_day_1', e.target.value)} /></div>
              {form.cadence === 'semi_monthly' && (
                <div className="field"><label>Billing day 2</label>
                  <input type="number" min="1" max="31" value={form.billing_day_2} onChange={(e) => setField('billing_day_2', e.target.value)} /></div>
              )}
            </div>

            <div className="form-grid">
              <div className="field"><label>Start date</label>
                <input type="date" value={form.start_date} onChange={(e) => setField('start_date', e.target.value)} /></div>
              <div className="field"><label>End date (optional)</label>
                <input type="date" value={form.end_date} onChange={(e) => setField('end_date', e.target.value)} /></div>
              <div className="field"><label>Net days (due)</label>
                <input type="number" min="0" value={form.net_days} onChange={(e) => setField('net_days', e.target.value)} /></div>
            </div>

            <label className="checkrow">
              <input type="checkbox" checked={!!form.auto_finalize || !!form.auto_charge} disabled={!!form.auto_charge}
                onChange={(e) => setField('auto_finalize', e.target.checked)} />
              <span>Automatically finalize &amp; post generated invoices. Uncheck to create them as drafts for review.</span>
            </label>

            {stripeEnabled && (
              <label className="checkrow">
                <input type="checkbox" checked={!!form.auto_charge} onChange={(e) => setField('auto_charge', e.target.checked)} />
                <span>Auto-charge the customer's saved card when each invoice is generated. Requires a card on file for the
                  customer (add one on their page); invoices finalize automatically when this is on.</span>
              </label>
            )}

            <h3 className="form-section">Line items billed each period</h3>
            <div className="li">
              <div className="li__head"><span>Item</span><span>Description</span><span>Qty</span><span>Unit price</span><span>Tax</span><span className="li__amt">Amount</span><span /></div>
              {lines.map((l, i) => {
                const amt = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
                return (
                  <div className="li__row" key={i}>
                    <select className="li__item-sel" value={l.item_id || ''} onChange={(e) => selectItem(i, e.target.value)}>
                      <option value="">— Custom —</option>
                      {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </select>
                    <input value={l.description} placeholder="Description" onChange={(e) => setLine(i, 'description', e.target.value)} />
                    <input type="number" step="0.01" value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} />
                    <input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, 'unit_price', e.target.value)} />
                    <input type="checkbox" checked={l.taxable !== false} onChange={(e) => setLine(i, 'taxable', e.target.checked)} />
                    <span className="num li__amt">{money(amt)}</span>
                    <button type="button" className="li__rm" onClick={() => removeLine(i)} aria-label="Remove">×</button>
                  </div>
                );
              })}
            </div>
            <div className="li__addrow"><button type="button" className="btn btn--sm" onClick={addLine}>+ Add line</button></div>

            <div className="totals">
              <div className="totals__row"><span>Subtotal</span><span className="num">{money(totals.subtotal)}</span></div>
              <div className="totals__row"><span>Tax{taxRate ? ` (${(taxRate * 100).toFixed(2)}%)` : ''}</span><span className="num">{money(totals.tax)}</span></div>
              <div className="totals__row totals__row--grand"><span>Per period</span><span className="num">{money(totals.total)}</span></div>
            </div>

            <div className="field" style={{ marginTop: 16 }}><label>Invoice terms / notes</label>
              <textarea rows={2} value={form.terms} onChange={(e) => setField('terms', e.target.value)} /></div>

            {/* status actions */}
            {editable && current && mode === 'edit' && (
              <div className="quote-actions">
                {current.status === 'active' && <button className="btn btn--sm" onClick={() => setStatus('paused')} disabled={saving}>Pause</button>}
                {current.status === 'paused' && <button className="btn btn--sm btn--primary" onClick={() => setStatus('active')} disabled={saving}>Resume</button>}
                {current.status !== 'cancelled' && <button className="btn btn--sm btn--danger" onClick={() => setStatus('cancelled')} disabled={saving}>Cancel contract</button>}
              </div>
            )}

            {/* uploaded signed copy / scan — existing contracts only */}
            {current && mode === 'edit' && (
              <>
                <h3 className="form-section">Signed copy</h3>
                <p className="hint" style={{ marginBottom: 10 }}>Upload a PDF or scan of the signed contract. The customer can view and download it from their portal.</p>
                {docs.length > 0 && (
                  <div className="cdocs">
                    {docs.map((d) => (
                      <div className="cdocs__row" key={d.id}>
                        <a className="cdocs__name" href={`/api/contracts/documents/${d.id}`} target="_blank" rel="noreferrer">{d.filename}</a>
                        <span className="cdocs__meta">{date(d.created_at)}{d.size ? ` · ${(Number(d.size) / 1024 / 1024).toFixed(2)} MB` : ''}</span>
                        <a className="btn btn--sm" href={`/api/contracts/documents/${d.id}?download=1`}>Download</a>
                        {editable && <button className="btn btn--sm btn--danger" onClick={() => deleteDoc(d.id)} disabled={docBusy}>Remove</button>}
                      </div>
                    ))}
                  </div>
                )}
                {editable && (
                  <label className="btn btn--sm" style={{ marginTop: 10, display: 'inline-block', cursor: 'pointer' }}>
                    {docBusy ? 'Uploading…' : (docs.length ? 'Upload another' : 'Upload signed copy')}
                    <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }} disabled={docBusy}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadDoc(f); }} />
                  </label>
                )}
                {docErr && <div className="form-error" style={{ marginTop: 8 }}>{docErr}</div>}
              </>
            )}

            {/* billing history */}
            {current?.runs?.length > 0 && (
              <>
                <h3 className="form-section">Billing history</h3>
                <div className="runs">
                  {current.runs.map((r) => (
                    <div className="runs__row" key={r.id}>
                      <span>{date(r.scheduled_date)}</span>
                      <span className="runs__inv">{r.invoice_number || '—'}</span>
                      <StatusBadge status={r.status} />
                      <span className="num">{r.total != null ? money(r.total) : '—'}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
